import { useState } from "react";
import { ArrowUpRight, Search, Calendar, Clock } from "lucide-react";

const posts = [
  {
    title: "Why BSS Modernisation Keeps Failing (And How to Get It Right)",
    excerpt: "Most BSS transformations stall at integration. We break down the five anti-patterns we've seen across 40+ rollouts, and what a recoverable plan actually looks like.",
    category: "BSS",
    date: "Nov 28, 2025",
    read: "8 min read",
    image: "https://images.pexels.com/photos/8640331/pexels-photo-8640331.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    title: "Pre-ATP Readiness: A Checklist Operators Actually Use",
    excerpt: "Environments that look green on paper still break on day one. Here's the pragmatic checklist our testing leads run before every ATP.",
    category: "Testing",
    date: "Nov 14, 2025",
    read: "6 min read",
    image: "https://images.unsplash.com/photo-1594345558880-804452ebc6b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMDNkJTIwZmliZXIlMjBvcHRpYyUyMGJsdWV8ZW58MHx8fHwxNzc3Mjc5MjAzfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Managed Services in Telecom: Beyond the SLA Theatre",
    excerpt: "SLAs don't matter if the partner doesn't understand the telecom stack. We share what operators should demand from a managed services contract.",
    category: "Managed Services",
    date: "Oct 30, 2025",
    read: "7 min read",
    image: "https://images.pexels.com/photos/12712432/pexels-photo-12712432.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    title: "Designing a Pricing Plan That Doesn't Break Your Billing",
    excerpt: "Marketing wants creative plans. Billing needs them to be operationally sane. Here's the collaboration model that keeps both teams happy.",
    category: "Product",
    date: "Oct 12, 2025",
    read: "5 min read",
    image: "https://images.unsplash.com/photo-1762279389042-9439bfb6c155?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjB0ZWNobm9sb2d5JTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzcyNzkyMDN8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "PMO for Telecom: Governance That Doesn't Slow You Down",
    excerpt: "Heavy governance kills velocity. Light governance hides risk. Here's how our PMO leads strike the balance on multi-vendor rollouts.",
    category: "PMO",
    date: "Sep 27, 2025",
    read: "6 min read",
    image: "https://images.pexels.com/photos/36713443/pexels-photo-36713443.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    title: "Five KPIs Every Operator Should Track Post Go-Live",
    excerpt: "Launching is not finishing. These five KPIs separate operators who stabilise quickly from those who bleed revenue for months.",
    category: "Strategy",
    date: "Sep 10, 2025",
    read: "5 min read",
    image: "https://images.unsplash.com/photo-1594345558880-804452ebc6b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMDNkJTIwZmliZXIlMjBvcHRpYyUyMGJsdWV8ZW58MHx8fHwxNzc3Mjc5MjAzfDA&ixlib=rb-4.1.0&q=85",
  },
];

const categories = ["All", "BSS", "Testing", "Managed Services", "Product", "PMO", "Strategy"];

export default function BlogPage() {
  const [active, setActive] = useState("All");
  const [query, setQuery] = useState("");

  const filtered = posts.filter((p) => {
    const matchCat = active === "All" || p.category === active;
    const matchQ = p.title.toLowerCase().includes(query.toLowerCase()) || p.excerpt.toLowerCase().includes(query.toLowerCase());
    return matchCat && matchQ;
  });

  const [featured, ...rest] = filtered;

  return (
    <div data-testid="blog-page">
      {/* Hero */}
      <section className="relative pt-36 md:pt-48 pb-12 md:pb-16 text-center">
        <div className="absolute inset-0 grid-pattern" />
        <div className="absolute inset-0 radial-glow" />
        <div className="relative max-w-4xl mx-auto px-6">
          <span className="section-label">Insights</span>
          <h1 className="mt-6 font-semibold tracking-tight leading-[1.08] text-[2rem] sm:text-5xl md:text-6xl lg:text-7xl">
            The <span className="gradient-text">Moxizo</span> Journal
          </h1>
          <p className="mt-6 md:mt-7 text-sm sm:text-base md:text-lg text-gray-400 max-w-[34ch] sm:max-w-xl md:max-w-2xl mx-auto">
            Field notes from BSS rollouts, testing programs and managed services trenches — written by the consultants doing the work.
          </p>
        </div>
      </section>

      {/* Controls */}
      <section className="pb-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row md:items-center gap-5">
          <div className="relative md:max-w-sm w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              data-testid="blog-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles"
              className="input-base pl-11 rounded-full"
            />
          </div>
          <div className="flex flex-wrap gap-2 md:ml-auto">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 rounded-full text-sm transition-all border ${
                  active === c
                    ? "bg-cyan-400 text-black border-cyan-400 font-medium"
                    : "border-white/10 text-gray-400 hover:text-cyan-400 hover:border-cyan-400/40"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured */}
      {featured && (
        <section className="pb-10">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <a href="#" className="card-base block overflow-hidden grid md:grid-cols-2" data-testid="blog-featured">
              <div className="relative overflow-hidden">
                <img src={featured.image} alt={featured.title} className="w-full h-full min-h-[320px] object-cover transition-transform duration-700 hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#121214]/30" />
              </div>
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-3 text-xs text-gray-500 uppercase tracking-[0.2em]">
                  <span className="text-cyan-400">{featured.category}</span>
                  <span className="w-1 h-1 rounded-full bg-gray-600" />
                  <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {featured.date}</span>
                  <span className="w-1 h-1 rounded-full bg-gray-600" />
                  <span className="inline-flex items-center gap-1.5"><Clock className="w-3 h-3" /> {featured.read}</span>
                </div>
                <h2 className="mt-5 text-2xl md:text-3xl lg:text-4xl font-medium leading-tight">{featured.title}</h2>
                <p className="mt-4 text-gray-400 leading-relaxed">{featured.excerpt}</p>
                <span className="btn-ghost mt-8 w-fit">Read featured <ArrowUpRight className="w-4 h-4" /></span>
              </div>
            </a>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {rest.length === 0 ? (
            <div className="card-base p-12 text-center text-gray-400">No articles match your filters yet.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((p, i) => (
                <a key={p.title} href="#" className="card-base overflow-hidden flex flex-col group" data-testid={`blog-post-${i}`}>
                  <div className="overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-48 object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs text-gray-500 uppercase tracking-[0.18em]">
                      <span className="text-cyan-400">{p.category}</span>
                      <span className="w-1 h-1 rounded-full bg-gray-600" />
                      <span>{p.date}</span>
                    </div>
                    <h3 className="mt-4 text-lg font-medium leading-snug">{p.title}</h3>
                    <p className="mt-3 text-sm text-gray-400 leading-relaxed flex-1">{p.excerpt}</p>
                    <span className="mt-6 inline-flex items-center gap-1.5 text-sm text-cyan-400 group-hover:gap-2.5 transition-all">
                      Read more <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
